import { Outlet, NavLink, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { TID } from "@/constants/testIds";
import {
  LayoutGrid, ListTodo, ClipboardCheck, Users, LogOut, Bell, KeyRound, ChevronDown, Camera, Menu, AlertTriangle,
} from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import api from "@/api/client";
import PasswordChangeDialog from "@/components/PasswordChangeDialog";
import AvatarUpload from "@/components/AvatarUpload";
import Avatar from "@/components/Avatar";
import NotificationCenter from "@/components/NotificationCenter";
import AlarmOverlay from "@/components/AlarmOverlay";
import InstallPrompt from "@/components/InstallPrompt";
import ChatDrawer from "@/components/ChatDrawer";
import DailyLogReminder from "@/components/DailyLogReminder";
import PersonalNotesWidget from "@/components/PersonalNotesWidget";
import TimerReminderWidget from "@/components/TimerReminderWidget";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent,
  DropdownMenuItem, DropdownMenuSeparator, DropdownMenuLabel,
} from "@/components/ui/dropdown-menu";
import { MessageSquare, X as CloseIcon } from "lucide-react";
import { fmtDateTime, todayIstDmy } from "@/utils/date";
import { SidebarNav, buildNavSections } from "@/components/SidebarNav";
import { GlobalSearch } from "@/components/GlobalSearch";
import { ThemeToggle } from "@/components/ThemeToggle";
import { KeyboardShortcuts } from "@/components/KeyboardShortcuts";
import { FloatingNudges } from "@/components/FloatingNudges";
import { Keyboard } from "lucide-react";
import { PanelLeftClose, PanelLeftOpen, ChevronRight } from "lucide-react";

export default function AppLayout() {
  const { user, logout, updateUser } = useAuth();
  const nav = useNavigate();
  const location = useLocation();
  // Opening the app (fresh load / coming back) always lands on My Day.
  useEffect(() => {
    if (location.pathname === "/") nav("/my-day", { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const [notifs, setNotifs] = useState([]);
  const [pwOpen, setPwOpen] = useState(false);
  const [avatarOpen, setAvatarOpen] = useState(false);
  const [chatOpen, setChatOpen] = useState(false);
  // Users can dismiss the floating chat button — persists in localStorage.
  // A tiny "revive chat" pill shows in the bottom-left after hiding so it's
  // always one click away.
  const [chatHidden, setChatHidden] = useState(() => {
    try { return localStorage.getItem("chatButtonHidden") === "1"; } catch (_) { return false; }
  });
  const hideChatButton = () => {
    localStorage.setItem("chatButtonHidden", "1");
    setChatHidden(true);
  };
  const showChatButton = () => {
    localStorage.removeItem("chatButtonHidden");
    setChatHidden(false);
  };
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(() => {
    try { return localStorage.getItem("lim-sidebar-collapsed") === "1"; } catch { return false; }
  });
  const toggleCollapsed = () => setCollapsed((c) => {
    try { localStorage.setItem("lim-sidebar-collapsed", c ? "0" : "1"); } catch { /* ignore */ }
    return !c;
  });

  const unread = notifs.filter((n) => !n.read).length;
  const isManager = user?.role === "manager" || !!user?.is_super_admin;
  const closeSidebar = () => setSidebarOpen(false);

  // Lock page scroll while the mobile sidebar overlay is open
  useEffect(() => {
    if (sidebarOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => { document.body.style.overflow = ""; };
  }, [sidebarOpen]);

  const onLogout = async () => { await logout(); nav("/login", { replace: true }); };

  // ---- Vault access (Finance / Hot Leads) — sidebar links depend on this ----
  // Backend computes `can_access` per-caller by consulting the base RBAC AND
  // the vault allowlist, so adding/removing someone via the vault settings is
  // immediately reflected here on the next page load.
  const [canFinance, setCanFinance] = useState(false);
  const [canHotLeads, setCanHotLeads] = useState(false);
  useEffect(() => {
    if (!user) { setCanFinance(false); setCanHotLeads(false); return; }
    Promise.all([
      api.get("/vaults/finance").then((r) => setCanFinance(!!r.data?.can_access)).catch(() => setCanFinance(false)),
      api.get("/vaults/hot_leads").then((r) => setCanHotLeads(!!r.data?.can_access)).catch(() => setCanHotLeads(false)),
    ]);
  }, [user?.id]);

  const navSections = buildNavSections(user, { canFinance, canHotLeads });
  const crumb = (() => {
    let best = null;
    navSections.forEach((sec) => sec.items.forEach((it) => {
      const hit = it.to === "/" ? location.pathname === "/" : location.pathname.startsWith(it.to);
      if (hit && (!best || it.to.length > best.item.to.length)) best = { section: sec.title, item: it };
    }));
    if (!best && location.pathname.startsWith("/daily-log")) best = { section: "Work", item: { to: "/daily-log", label: "Daily Log", icon: ClipboardCheck } };
    return best;
  })();

  const markAllRead = async () => {
    try {
      await api.post("/notifications/read-all");
      setNotifs((prev) => prev.map((n) => ({ ...n, read: true })));
    } catch (e) { /* silent */ }
  };

  // ---- Missing-log hard block ----
  // Product rule: if yesterday (or the nearest previous working day) has no
  // filed log, the user cannot navigate anywhere other than /daily-log until
  // they close it out. Super-admins are exempt (they don't file logs).
  const [logBlock, setLogBlock] = useState({ blocked: false, missing_date: null });
  const refreshLogBlock = useCallback(async () => {
    if (!user || user.is_super_admin) {
      setLogBlock({ blocked: false, missing_date: null });
      return { blocked: false };
    }
    try {
      const { data } = await api.get("/me/log-block");
      setLogBlock(data || { blocked: false });
      return data || { blocked: false };
    } catch {
      return { blocked: false };
    }
  }, [user?.id, user?.is_super_admin]);
  useEffect(() => { refreshLogBlock(); }, [refreshLogBlock]);

  // While blocked, force every non-/daily-log route back to the Daily Log for
  // the missing date so the person literally cannot slip past.
  useEffect(() => {
    if (!logBlock.blocked || !logBlock.missing_date) return;
    const onDailyLog = location.pathname === "/daily-log" || location.pathname === "/leaves";
    if (!onDailyLog) {
      nav(`/daily-log?date=${logBlock.missing_date}&block=1`, { replace: true });
    }
  }, [logBlock, location.pathname, nav]);

  return (
    <div className="min-h-screen flex text-white">
      <NotificationCenter onNotifs={setNotifs} />
      <KeyboardShortcuts />
      {!logBlock.blocked && <FloatingNudges />}
      <AlarmOverlay />
      <InstallPrompt />

      {/* Mobile sidebar backdrop */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/60 lg:hidden animate-fade-in-up"
          onClick={closeSidebar}
          data-testid="sidebar-backdrop"
        />
      )}

      {/* Sidebar — slide-in on mobile, static on desktop, collapsible to icons */}
      <aside
        data-testid="app-sidebar"
        className={`fixed lg:sticky lg:top-0 lg:h-screen inset-y-0 left-0 z-50 w-64 ${collapsed ? "lg:w-16" : "lg:w-64"} shrink-0 border-r border-white/10 bg-sidebar flex flex-col transform transition-[transform,width] duration-200 ${
          sidebarOpen ? "translate-x-0" : "-translate-x-full lg:translate-x-0"
        }`}
      >
        <div className={`h-[60px] flex items-center border-b border-white/10 ${collapsed ? "lg:justify-center px-3" : "px-4"} gap-2.5`}>
          <img src="/flow-logo-small.png" alt="Flow" className="h-8 w-8 object-contain rounded-md shrink-0" />
          <div className={`min-w-0 ${collapsed ? "lg:hidden" : ""}`}>
            <div className="font-hero text-base leading-none">Flow</div>
            <div className="text-[0.7rem] text-white/50 leading-none mt-1">Life Inside Media</div>
          </div>
        </div>
        <SidebarNav sections={navSections} collapsed={collapsed && !sidebarOpen} onNavigate={closeSidebar} />
        <div className="hidden lg:block p-2.5 border-t border-white/10">
          <button
            type="button"
            onClick={toggleCollapsed}
            data-testid="sidebar-collapse-toggle"
            title={collapsed ? "Expand sidebar" : "Collapse sidebar"}
            className={`w-full h-9 flex items-center gap-2 rounded-md text-sm text-white/60 hover:text-white hover:bg-white/5 transition-colors ${collapsed ? "justify-center" : "px-3"}`}
          >
            {collapsed ? <PanelLeftOpen size={17} /> : <><PanelLeftClose size={17} /> Collapse</>}
          </button>
        </div>
      </aside>

      {/* Main */}
      <main className="flex-1 flex flex-col min-w-0">
        <header className="sticky top-0 z-30 flex items-center gap-3 px-3 sm:px-6 h-[60px] border-b border-white/10 bg-surface">
          <button
            onClick={() => setSidebarOpen(true)}
            className="lg:hidden grid place-items-center h-9 w-9 rounded-md text-white/70 hover:bg-white/5 transition-colors"
            aria-label="Open menu"
            data-testid="sidebar-open"
          >
            <Menu size={19} />
          </button>
          <div className="flex-1 min-w-0 flex items-center gap-3">
            {crumb && (
              <nav aria-label="Breadcrumb" data-testid="breadcrumb" className="hidden lg:flex items-center gap-1.5 text-sm shrink-0 pr-2 mr-1 border-r border-white/10">
                <span className="text-white/50">{crumb.section}</span>
                <ChevronRight size={14} className="text-white/30" />
                <span className="font-semibold text-white inline-flex items-center gap-1.5" data-testid="breadcrumb-current">
                  <crumb.item.icon size={15} className="text-[#E11D2E]" /> {crumb.item.label}
                </span>
              </nav>
            )}
            <GlobalSearch sections={navSections} />
            <div className="text-xs text-white/45 font-mono hidden xl:block">{todayIstDmy()}</div>
          </div>
          <div className="flex items-center gap-1.5 sm:gap-2">
            {chatHidden && (
              <button
                onClick={showChatButton}
                data-testid="chat-restore-header-btn"
                title="Show team chat"
                aria-label="Show team chat"
                className="inline-flex items-center gap-1.5 h-9 px-2.5 rounded-md border border-white/10 hover:bg-white/5 text-xs font-medium text-white/70 transition-colors"
              >
                <MessageSquare size={14} /> Chat
              </button>
            )}
            <button
              type="button"
              onClick={() => window.dispatchEvent(new KeyboardEvent("keydown", { key: "?" }))}
              data-testid="shortcuts-btn"
              title="Keyboard shortcuts (?)"
              aria-label="Keyboard shortcuts"
              className="hidden md:grid h-9 w-9 place-items-center rounded-md border border-white/10 text-white/70 hover:text-white hover:bg-white/5 transition-colors"
            >
              <Keyboard size={16} />
            </button>
            <ThemeToggle />
            <div className="relative group" data-testid="notifications-bell">
              <button onClick={markAllRead} title="Notifications" className="relative h-9 w-9 grid place-items-center rounded-md border border-white/10 hover:bg-white/5 transition-colors" aria-label="Notifications">
                <Bell size={16} className="text-white/70" />
                {unread > 0 && (
                  <span className="absolute -top-1 -right-1 min-w-[16px] h-4 px-1 rounded-full bg-[#E11D2E] text-[0.7rem] font-semibold grid place-items-center">
                    {unread}
                  </span>
                )}
              </button>
              <div className="hidden group-hover:block absolute right-0 top-full mt-2 w-[86vw] sm:w-80 max-h-96 overflow-y-auto scrollbar-thin bg-surface border border-white/10 rounded-lg p-2 z-40 shadow-float">
                {notifs.length === 0 ? (
                  <div className="text-xs text-white/40 p-3">No notifications</div>
                ) : notifs.slice(0, 10).map((n) => (
                  <div key={n.id} className={`p-2.5 rounded-md text-xs ${n.read ? "text-white/50" : "text-white bg-white/5"}`}>
                    <div>{n.text}</div>
                    <div className="text-white/40 mt-0.5 font-mono">{fmtDateTime(n.created_at)}</div>
                  </div>
                ))}
              </div>
            </div>
            <div className="w-px h-6 bg-white/10 mx-1 hidden sm:block" />
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                data-testid="user-menu-trigger"
                className="flex items-center gap-2 p-1 pr-2 rounded-md hover:bg-white/5 transition-colors"
              >
                <Avatar user={user} size={30} />
                <div className="hidden md:block text-left min-w-0 max-w-[160px]">
                  <div className="text-sm font-medium truncate leading-tight">{user?.name}</div>
                  <div className="text-xs text-white/50 truncate leading-tight">
                    {user?.is_super_admin
                      ? "Super Admin"
                      : user?.is_admin
                      ? `Admin · ${user?.team}`
                      : `${user?.title || user?.role} · ${user?.team}`}
                  </div>
                </div>
                <ChevronDown size={14} className="text-white/40 hidden md:block" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="bg-surface border-white/10 text-white w-60 shadow-float">
              <DropdownMenuLabel className="text-white/60 truncate font-normal">{user?.email}</DropdownMenuLabel>
              <DropdownMenuSeparator className="bg-white/10" />
              <DropdownMenuItem data-testid="menu-change-photo" onClick={() => setAvatarOpen(true)} className="cursor-pointer focus:bg-white/5">
                <Camera size={14} className="mr-2" /> Change photo
              </DropdownMenuItem>
              <DropdownMenuItem data-testid="menu-change-password" onClick={() => setPwOpen(true)} className="cursor-pointer focus:bg-white/5">
                <KeyRound size={14} className="mr-2" /> Change password
              </DropdownMenuItem>
              <DropdownMenuSeparator className="bg-white/10" />
              <DropdownMenuItem data-testid={TID.sidebarLogout} onClick={onLogout} className="cursor-pointer text-red-600 focus:bg-red-500/10">
                <LogOut size={14} className="mr-2" /> Sign out
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          </div>
        </header>
        {/* Hard-block banner — sticky, non-dismissable while active. */}
        {logBlock.blocked && logBlock.missing_date && (
          <div
            className="sticky top-[60px] z-20 bg-rose-500/15 border-y border-rose-500/40 text-rose-100 px-3 sm:px-6 py-2.5 flex items-start sm:items-center gap-3 text-sm"
            data-testid="log-block-banner"
          >
            <AlertTriangle size={18} className="shrink-0 text-rose-300 mt-0.5 sm:mt-0" />
            <div className="flex-1">
              <div className="font-medium">Your daily log for {logBlock.missing_date} is still pending.</div>
              <div className="text-rose-200/80 text-xs mt-0.5">
                You can't access the rest of LIM Flow until this log is filed.
                Save it below and the app will unlock automatically.
              </div>
            </div>
            <button type="button" onClick={() => nav(location.pathname === "/leaves" ? `/daily-log?date=${logBlock.missing_date}&block=1` : "/leaves")}
              data-testid="log-block-leaves-link" className="shrink-0 h-8 px-3 rounded-md border border-rose-400/50 text-xs font-medium hover:bg-rose-500/10">
              {location.pathname === "/leaves" ? "Back to Daily Log" : "Apply for leave"}
            </button>
          </div>
        )}
        <div className="flex-1 overflow-y-auto overflow-x-hidden scrollbar-thin pb-44 lg:pb-0 lg:[&>div]:px-8 lg:[&>div]:py-7" data-testid="page-canvas">
          <Outlet context={{ logBlock, refreshLogBlock }} />
        </div>
      </main>

      <PasswordChangeDialog open={pwOpen} onOpenChange={setPwOpen} />
      <AvatarUpload
        open={avatarOpen}
        onOpenChange={setAvatarOpen}
        user={user}
        onUpdated={(u) => updateUser({ avatar_url: u.avatar_url })}
      />
      <ChatDrawer open={chatOpen} onClose={() => setChatOpen(false)} />
      {!user?.is_super_admin && <DailyLogReminder />}
      {!user?.is_super_admin && <TimerReminderWidget />}
      <PersonalNotesWidget />

      {/* Mobile bottom nav — persistent primary navigation for phones */}
      <nav
        className="lg:hidden fixed bottom-0 inset-x-0 z-40 bg-surface border-t border-white/10 flex items-stretch"
        style={{ paddingBottom: "env(safe-area-inset-bottom)" }}
        data-testid="mobile-bottom-nav"
      >
        <BottomTab to="/" icon={LayoutGrid} label="Board" testid="bnav-whiteboard" />
        <BottomTab to="/my-tasks" icon={ListTodo} label="My Tasks" testid="bnav-my-tasks" />
        {!user?.is_super_admin && <BottomTab to="/daily-log" icon={ClipboardCheck} label="Daily" testid="bnav-daily" />}
        {isManager && user?.is_super_admin && <BottomTab to="/team" icon={Users} label="Team" testid="bnav-team" />}
        <button
          onClick={() => setSidebarOpen(true)}
          data-testid="bnav-menu"
          className="flex-1 flex flex-col items-center justify-center gap-0.5 py-2 text-white/60 hover:text-white transition-colors"
        >
          <Menu size={18} />
          <span className="text-[0.7rem] font-semibold">Menu</span>
        </button>
      </nav>

      {/* Floating chat button — bottom-right on mobile (above the bottom nav), bottom-left on desktop.
          When user dismisses it via the tiny × on the button, the "Chat" button in the top
          header takes over as the restore control (always visible above the OS dock). */}
      {!chatHidden && (
        <div className={`fixed bottom-20 right-4 lg:bottom-6 lg:right-6 z-50 flex items-center gap-1 ${chatOpen ? "hidden" : ""}`}>
          <button
            onClick={() => setChatOpen(true)}
            data-testid="chat-open-btn"
            className="h-12 lg:h-14 w-12 lg:w-auto lg:px-5 rounded-full lim-gradient shadow-float flex items-center justify-center gap-2 hover:brightness-110 transition"
            title="Open team chat"
            aria-label="Open team chat"
          >
            <MessageSquare size={20} className="text-white" />
            <span className="text-sm font-display text-white hidden lg:inline">Team Chat</span>
          </button>
          <button
            onClick={hideChatButton}
            data-testid="chat-hide-btn"
            title="Hide chat button"
            aria-label="Hide chat button"
            className="h-6 w-6 -ml-2 -mt-8 rounded-full bg-surface border border-white/15 flex items-center justify-center text-white/70 hover:text-white shadow-float transition-colors"
          >
            <CloseIcon size={11} />
          </button>
        </div>
      )}
    </div>
  );
}

function BottomTab({ to, icon: Icon, label, testid }) {
  return (
    <NavLink
      to={to}
      end
      data-testid={testid}
      className={({ isActive }) =>
        `flex-1 flex flex-col items-center justify-center gap-0.5 py-2 transition-colors ${
          isActive ? "text-[#E11D2E]" : "text-white/60 hover:text-white"
        }`
      }
    >
      <Icon size={18} />
      <span className="text-[0.7rem] font-semibold">{label}</span>
    </NavLink>
  );
}
